/**
 * FetchXML for configured sections, built from what the overlay says: a node's own `fetchXml`
 * with `{{placeholders}}`, or a saved view narrowed by its `rowSource`. Pure string work on the
 * query, so what a section asks the server for is provable from a fixture.
 */

import {
  OverlayCard,
  OverlayDataSource,
  OverlayLookupColumn,
  OverlayLookupView,
  OverlayNode,
  OverlayRowSource,
  OverlaySchemaEntry,
} from '@omni/interfaces/app-ui/overlay.interface';
import type { AppUiFieldOption } from './app-ui-merge';
import { isBlank } from './app-ui-expression';
import { FORMATTED } from '@omni/utility/dynamics-row.util';

/** A `{{path}}` token in configured FetchXML. Global: use with `replace` or `matchAll` only. */
export const PLACEHOLDER = /\{\{\s*([\w.@]+)\s*\}\}/g;
const CONDITION = /<condition\b[^>]*?(?:\/>|>[\s\S]*?<\/condition>)/g;
const INNER_LINK =
  /<link-entity\b[^>]*?(?:\/>|>(?:(?!<link-entity\b)[\s\S])*?<\/link-entity>)/g;

export function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Fills each `{{path}}`; null when any is missing, so a query never runs half-filled. */
export function fillFetchXml(
  template: string,
  resolve: (path: string) => unknown,
): string | null {
  let missing = false;
  const filled = template.replace(PLACEHOLDER, (_match, path: string) => {
    const value = resolve(path);
    if (value === null || value === undefined || value === '') {
      missing = true;
      return '';
    }
    return escapeXml(String(value));
  });
  return missing ? null : filled;
}

/** The query's root entity. */
export function fetchEntity(fetchXml: string): string | null {
  return /<entity\s+name="([^"]+)"/.exec(fetchXml)?.[1] ?? null;
}

/** `customerid/salesorder.customerid` → the record attribute to read, the entity, and the column to match. */
export function parseRowSourcePath(
  path: string | undefined,
): { parentAttr: string; entity: string; attribute: string } | null {
  const match = /^([\w]+)\/([\w]+)\.([\w]+)$/.exec(path ?? '');
  return match
    ? { parentAttr: match[1], entity: match[2], attribute: match[3] }
    : null;
}

/** The node's `schema` entry that describes its rows, e.g. `schema.history`. */
export function rowSchema(node: OverlayNode): OverlaySchemaEntry | null {
  return (
    Object.values(node.schema ?? {}).find(
      (entry) => typeof entry?.entity === 'string',
    ) ?? null
  );
}

/**
 * A query from the node's own description: the entity and its `…Attr` columns from `schema`
 * (`line…` ones belong to the line entity), filtered, ordered and capped by `rowSource`. Null
 * without a `rowSource.path`, so a node never lists every record of an entity.
 */
export function schemaFetchXml(node: OverlayNode): string | null {
  const path = parseRowSourcePath(node.rowSource?.path);
  const schema = rowSchema(node);
  const entity = schema?.entity ?? path?.entity;
  if (!path || !entity || entity !== path.entity) return null;
  const columns = Object.entries(schema ?? {})
    .filter(
      ([key, value]) =>
        /Attr$/.test(key) && !/^line/.test(key) && /^\w+$/.test(String(value)),
    )
    .map(([, value]) => value);
  const attributes = [...new Set(columns)]
    .map((name) => `<attribute name="${name}" />`)
    .join('');
  return `<fetch><entity name="${entity}">${attributes}</entity></fetch>`;
}

/** Index just inside the root entity, before its first child element. */
export function rootEntityBody(
  fetchXml: string,
): { start: number; end: number } | null {
  const open = /<entity\s+name="[^"]+"[^>]*?(?<!\/)>/.exec(fetchXml);
  const end = fetchXml.lastIndexOf('</entity>');
  return open && end > 0 ? { start: open.index + open[0].length, end } : null;
}

/** The root entity's own children, with link-entities blanked so their content cannot match. */
export function rootLevel(
  fetchXml: string,
  body: { start: number; end: number },
): string {
  let inner = fetchXml.slice(body.start, body.end);
  let previous: string;
  do {
    previous = inner;
    inner = inner.replace(INNER_LINK, (m) => ' '.repeat(m.length));
  } while (inner !== previous);
  return inner;
}

/** The root entity's body without its own `tag` elements; link-entities keep theirs. */
function withoutRootTags(
  fetchXml: string,
  body: { start: number; end: number },
  tag: RegExp,
): string {
  let inner = fetchXml.slice(body.start, body.end);
  for (const match of [...rootLevel(fetchXml, body).matchAll(tag)].reverse())
    inner =
      inner.slice(0, match.index) +
      inner.slice((match.index ?? 0) + match[0].length);
  return inner;
}

/** Adds a `<filter>` to the root entity, alongside whatever filter it has. */
export function addFilter(fetchXml: string, filter: string): string {
  return addToRootEntity(fetchXml, filter);
}

/** Adds an element (a `<filter>`, a `<link-entity>`) to the root entity, first among its children. */
export function addToRootEntity(fetchXml: string, xml: string): string {
  const body = rootEntityBody(fetchXml);
  if (!body) return fetchXml;
  return fetchXml.slice(0, body.start) + xml + fetchXml.slice(body.start);
}

/** Adds `attribute = value` to the root entity, alongside whatever filter it has. */
export function addCondition(
  fetchXml: string,
  attribute: string,
  value: string,
): string {
  return addFilter(
    fetchXml,
    `<filter type="and"><condition attribute="${attribute}" operator="eq" value="${escapeXml(value)}" /></filter>`,
  );
}

/** Replaces the root entity's ordering with `orderBy` (`-` for descending). */
export function setOrder(fetchXml: string, orderBy: string): string {
  return setOrders(fetchXml, [
    {
      attribute: orderBy.replace(/^-/, ''),
      descending: orderBy.startsWith('-'),
    },
  ]);
}

/** Replaces the root entity's ordering with `orders`, in turn. */
export function setOrders(
  fetchXml: string,
  orders: readonly { attribute: string; descending?: boolean }[],
): string {
  const body = rootEntityBody(fetchXml);
  if (!body) return fetchXml;
  const inner = withoutRootTags(fetchXml, body, /<order\b[^>]*\/>/g);
  const order = orders
    .map(
      (o) =>
        `<order attribute="${escapeXml(o.attribute)}" descending="${!!o.descending}" />`,
    )
    .join('');
  return (
    fetchXml.slice(0, body.start) + order + inner + fetchXml.slice(body.end)
  );
}

/** At most `top` rows: paging and counting are dropped, which `top` does not allow beside it. */
export function setTop(fetchXml: string, top: number): string {
  return fetchXml.replace(/<fetch\b([^>]*)>/, (_m, attrs: string) => {
    const kept = attrs.replace(
      /\s(top|count|page|paging-cookie|returntotalrecordcount)="[^"]*"/g,
      '',
    );
    return `<fetch${kept} top="${Math.max(1, Math.trunc(top))}">`;
  });
}

/** Adds each attribute the root entity does not select yet, when the entity has it. */
export function ensureAttributes(
  fetchXml: string,
  attributes: readonly string[],
  exists: (attribute: string) => boolean,
): string {
  const body = rootEntityBody(fetchXml);
  if (!body) return fetchXml;
  if (/<all-attributes\s*\/>/.test(rootLevel(fetchXml, body))) return fetchXml;
  const selected = new Set(
    [...rootLevel(fetchXml, body).matchAll(/<attribute\s+name="([^"]+)"/g)].map(
      (m) => m[1],
    ),
  );
  const added = [...new Set(attributes)].filter(
    (name) => !selected.has(name) && exists(name),
  );
  return (
    fetchXml.slice(0, body.start) +
    added.map((name) => `<attribute name="${name}" />`).join('') +
    fetchXml.slice(body.start)
  );
}

/** The root entity reading every column: its own `<attribute>`s give way to `<all-attributes />`. */
export function selectAll(fetchXml: string): string {
  const body = rootEntityBody(fetchXml);
  if (!body) return fetchXml;
  if (/<all-attributes\s*\/>/.test(rootLevel(fetchXml, body))) return fetchXml;
  const inner = withoutRootTags(fetchXml, body, /<attribute\b[^>]*\/>/g);
  return `${fetchXml.slice(0, body.start)}<all-attributes />${inner}${fetchXml.slice(body.end)}`;
}

/** The query without the conditions that read a `{{token}}`, for a caller with no record to fill them from. */
export function withoutTokens(fetchXml: string): string {
  return fetchXml.replace(CONDITION, (condition) =>
    /\{\{[^}]*\}\}/.test(condition) ? '' : condition,
  );
}

/** Every attribute a card reads, footer tokens included. */
export function cardAttributes(
  card: OverlayCard | undefined,
  idAttr?: string,
): string[] {
  const footer = [...(card?.footer ?? '').matchAll(PLACEHOLDER)].map(
    (m) => m[1],
  );
  return [
    idAttr,
    card?.title,
    card?.badge,
    ...cardFields(card),
    card?.value,
    ...footer,
  ].filter((name): name is string => !!name);
}

/** How many v1.24 `fields` a card shows between its title and footer. */
const CARD_FIELDS_SHOWN = 4;

/** The columns between a card's title and footer: v1.24 `fields`, else `meta`. */
export function cardFields(card: OverlayCard | undefined): string[] {
  return card?.fields?.length
    ? card.fields.slice(0, CARD_FIELDS_SHOWN)
    : (card?.meta ?? []);
}

/** A dataset's query as a list runs it: `{{name}}` reads its column, and `orderBy` replaces the order. */
export function datasetFetchXml(source: OverlayDataSource): string | null {
  if (
    (source.kind ?? 'fetchXml') !== 'fetchXml' ||
    typeof source.fetchXml !== 'string' ||
    !source.fetchXml.trim()
  )
    return null;
  const columns = new Map(
    (source.placeholders ?? []).map((p) => [p.name, p.column]),
  );
  const fetchXml = source.fetchXml.replace(
    PLACEHOLDER,
    (token, name: string) => {
      const column = columns.get(name);
      return column ? `{{${column}}}` : token;
    },
  );
  return source.orderBy?.length
    ? setOrders(fetchXml, source.orderBy)
    : fetchXml;
}

/**
 * A `historyPanel` naming a dataset lists that dataset's query in place of the customer's orders;
 * one naming a dataset the screen lacks keeps the built-in list.
 */
export function withDataSource(
  node: OverlayNode,
  sources: readonly OverlayDataSource[] | undefined,
): OverlayNode {
  const fetchXml = namedDataset(node, sources);
  return fetchXml ? { ...node, fetchXml } : node;
}

function namedDataset(
  node: OverlayNode,
  sources: readonly OverlayDataSource[] | undefined,
): string | null {
  const source = node.dataSource
    ? sources?.find((s) => s.id === node.dataSource)
    : undefined;
  return source ? datasetFetchXml(source) : null;
}

const INNER_FILTER =
  /<filter\b([^>]*?)(?:\/>|>((?:(?!<filter\b)[\s\S])*?)<\/filter>)/g;

function tagAttr(attrs: string, name: string): string | null {
  return (
    new RegExp(`\\b${name}\\s*=\\s*["']([^"']*)["']`).exec(attrs)?.[1] ?? null
  );
}

/** Replaces matches of `pattern` innermost first until none is left. */
function collapse(
  xml: string,
  pattern: RegExp,
  by: (attrs: string, inner: string) => string,
): string {
  let previous: string;
  let current = xml;
  do {
    previous = current;
    current = current.replace(pattern, (_m, attrs = '', inner = '') =>
      by(String(attrs), String(inner)),
    );
  } while (current !== previous);
  return current;
}

/** The conditions every row of the root table meets: none under an `or` or a link-entity. */
function rootAndConditions(fetchXml: string): string[] {
  const unlinked = collapse(fetchXml, INNER_LINK, () => '');
  const flat = collapse(unlinked, INNER_FILTER, (attrs, inner) =>
    tagAttr(attrs, 'type') === 'or' ? '' : inner,
  );
  return flat.match(CONDITION) ?? [];
}

function stateTest(
  operator: string,
  values: string[],
): (state: number) => boolean {
  const numbers = values.map(Number);
  switch (operator) {
    case 'eq':
      return (state) => state === numbers[0];
    case 'ne':
    case 'neq':
      return (state) => state !== numbers[0];
    case 'in':
      return (state) => numbers.includes(state);
    case 'not-in':
      return (state) => !numbers.includes(state);
    default:
      return () => true;
  }
}

/**
 * What a query's `statecode` conditions on its own table let through, for rows that never came
 * from it (the device's records). Null when it sets none; a condition under an `or` is not read.
 */
export function stateFilter(
  fetchXml: string,
): ((state: number) => boolean) | null {
  const tests = rootAndConditions(fetchXml).flatMap((condition) => {
    const open = /^<condition\b[^>]*/.exec(condition)?.[0] ?? '';
    // `entityname` points the condition at a linked table's column.
    if (
      tagAttr(open, 'attribute') !== 'statecode' ||
      tagAttr(open, 'entityname') !== null
    )
      return [];
    const value = tagAttr(open, 'value');
    const values =
      value === null
        ? [...condition.matchAll(/<value>\s*([^<]*?)\s*<\/value>/g)].map(
            (m) => m[1],
          )
        : [value];
    return [stateTest(tagAttr(open, 'operator') ?? 'eq', values)];
  });
  return tests.length ? (state) => tests.every((test) => test(state)) : null;
}

/**
 * A view's query narrowed to this record and shaped by `rowSource`. Null when the record does not
 * yet hold the value the path filters on.
 */
export function narrowView(
  viewFetchXml: string,
  rowSource: OverlayRowSource | undefined,
  resolve: (attributePath: string) => unknown,
): string | null {
  let fetchXml = viewFetchXml;
  const path = parseRowSourcePath(rowSource?.path);
  if (path) {
    const value = resolve(path.parentAttr);
    if (value === null || value === undefined || value === '') return null;
    const narrowed = addCondition(fetchXml, path.attribute, String(value));
    // A query that cannot be narrowed to the record must not run: it would list everyone's rows.
    if (narrowed === fetchXml) return null;
    fetchXml = narrowed;
  }
  if (rowSource?.orderBy) fetchXml = setOrder(fetchXml, rowSource.orderBy);
  if (rowSource?.top) fetchXml = setTop(fetchXml, rowSource.top);
  return fetchXml;
}

/** A list's own FetchXML keeps its ordering and limit; without them it takes `rowSource`'s. */
export function withListOrder(
  fetchXml: string,
  rowSource: OverlayRowSource | undefined,
): string {
  let result = fetchXml;
  const body = rootEntityBody(result);
  if (body && rowSource?.orderBy && !/<order\b/.test(rootLevel(result, body)))
    result = setOrder(result, rowSource.orderBy);
  if (rowSource?.top && !/<fetch\b[^>]*\s(?:top|count)=/.test(result))
    result = setTop(result, rowSource.top);
  return result;
}

/**
 * A tab badge's FetchXML its list can run for its cards, so one query gives both the cards and
 * the count: one that lists rows of the list's own table. A count-only (aggregate) query cannot.
 */
export function badgeListQuery(
  node: OverlayNode,
  fetchXml: unknown,
): string | null {
  if (
    typeof fetchXml !== 'string' ||
    /<fetch\b[^>]*\baggregate\s*=\s*["']true["']/i.test(fetchXml)
  )
    return null;
  const entity =
    rowSchema(node)?.entity ?? parseRowSourcePath(node.rowSource?.path)?.entity;
  return !entity || fetchEntity(fetchXml) === entity ? fetchXml : null;
}

export type AppUiQuerySource =
  | { fetchXml: string }
  | { built: string }
  | { viewId: string };

/** Node types that list rows from a query. Others (a matrix, a summary) also carry `schema` / `rowSource` for their own datasets. */
const LIST_TYPES = new Set(['recordList', 'historyPanel']);

/**
 * What the Dynamics OrderHistory control assumes when a `historyPanel` names no rows of its own
 * (contract `schema.history` defaults): the record's customer's orders, newest first.
 */
const HISTORY_DEFAULTS = {
  rowSource: {
    path: 'customerid/salesorder.customerid',
    orderBy: '-createdon',
    top: 12,
  },
  schema: {
    history: {
      entity: 'salesorder',
      idAttr: 'salesorderid',
      nameAttr: 'ordernumber',
      customerAttr: 'customerid',
      dateAttr: 'createdon',
      statusAttr: 'statuscode',
      totalAttr: 'totalamount',
    },
  },
  card: {
    title: 'ordernumber',
    badge: 'statuscode',
    meta: ['createdon'],
    value: 'totalamount',
  },
};

/**
 * A `historyPanel` with the control's defaults filled in where the overlay leaves them out. They
 * describe orders, so a panel naming a dataset of another table takes none.
 */
export function withListDefaults(
  node: OverlayNode,
  sources?: readonly OverlayDataSource[],
): OverlayNode {
  if (node.type !== 'historyPanel' || querySource(node)) return node;
  const dataset = namedDataset(node, sources);
  if (
    dataset &&
    fetchEntity(dataset) !== HISTORY_DEFAULTS.schema.history.entity
  )
    return node;
  return {
    ...node,
    rowSource: {
      ...HISTORY_DEFAULTS.rowSource,
      ...node.rowSource,
    },
    schema: node.schema ?? HISTORY_DEFAULTS.schema,
    card: node.card ?? HISTORY_DEFAULTS.card,
  };
}

/** What a list node asks for: its own query, else one built from its `schema` and `rowSource`, else a view. */
export function querySource(node: OverlayNode): AppUiQuerySource | null {
  if (typeof node.fetchXml === 'string' && node.fetchXml.trim())
    return { fetchXml: node.fetchXml };
  if (!LIST_TYPES.has(String(node.type))) return null;
  const built = schemaFetchXml(node);
  if (built) return { built };
  const viewId = node.view?.id;
  return typeof viewId === 'string' && viewId ? { viewId } : null;
}

/** True when a tab page holds a section that queries the server; such lists are online only. */
export function isOnlineOnly(page: OverlayNode): boolean {
  const queries = (node: OverlayNode): boolean =>
    (node !== page && !!querySource(node)) ||
    (node.children ?? []).some(queries);
  return queries(page);
}

/**
 * A lookup's candidates from its view (contract v1.5): the published FetchXML with each bound
 * condition filled from the record, and each row shown by the view's columns.
 */
/** The picker searches on the device, so it holds this many candidates at most. */
export const LOOKUP_ROW_LIMIT = 500;

/** The query to run, or null when the view offers nothing for this record. */
export function lookupFetchXml(
  view: OverlayLookupView,
  resolve: (path: string) => unknown,
): string | null {
  if (!view.fetchXml) return null;
  let unbound = false;
  const pruned = view.fetchXml.replace(CONDITION, (condition) => {
    if (
      ![...condition.matchAll(PLACEHOLDER)].some((match) =>
        isBlank(resolve(match[1])),
      )
    )
      return condition;
    unbound = true;
    return '';
  });
  if (unbound && view.whenEmpty === 'none') return null;
  const filled = fillFetchXml(pruned, resolve);
  return filled === null ? null : setTop(filled, LOOKUP_ROW_LIMIT);
}

/** Each row as a choice: the first column is the label, the rest its detail. */
export function lookupOptions(
  view: OverlayLookupView,
  rows: Record<string, unknown>[],
  entity: string,
): AppUiFieldOption[] {
  const columns = view.columns ?? [];
  const searchable = searchableColumns(view, columns);
  return rows.flatMap((row) => {
    // Activity tables (appointment, task …) key their rows by `activityid`.
    const id = row[`${entity}id`] ?? row.activityid;
    if (typeof id !== 'string' || !id) return [];
    const texts = columns
      .filter((column) => !column.hidden)
      .map((column) => formattedCell(row, keyOf(column)));
    const detail = texts.slice(1).filter(Boolean).join(' · ');
    return [
      {
        value: id,
        label:
          texts[0] ||
          formattedCell(row, 'name') ||
          formattedCell(row, 'fullname') ||
          id,
        ...(detail ? { detail } : {}),
        search: searchable
          .map((column) => formattedCell(row, keyOf(column)))
          .filter(Boolean)
          .join(' '),
      },
    ];
  });
}

/** Columns publish marked; else those `searchable` names; else every column. */
function searchableColumns(
  view: OverlayLookupView,
  columns: OverlayLookupColumn[],
): OverlayLookupColumn[] {
  if (columns.some((column) => column.searchable !== undefined))
    return columns.filter((column) => column.searchable);
  const named = view.searchable;
  return named
    ? columns.filter(
        (column) =>
          named.includes(column.path) || named.includes(keyOf(column)),
      )
    : columns;
}

function keyOf(column: OverlayLookupColumn): string {
  return column.field ?? column.path;
}

/** What Dynamics shows for a cell: its formatted value, a lookup's name, else the raw value. */
export function formattedCell(
  row: Record<string, unknown>,
  key: string,
): string {
  const value =
    row[`${key}${FORMATTED}`] ??
    row[`_${key}_value${FORMATTED}`] ??
    row[`${key}@formatted`] ??
    row[key];
  return isBlank(value) ? '' : String(value);
}

/** One N:1 step of a column path, as a `<link-entity>`. */
export interface AppUiLookupHop {
  entity: string;
  from: string;
  to: string;
}

/** `a.b[account].c` → lookups `a`, `b` (on table `account`), then attribute `c`. At most three lookups. */
export function parseColumnPath(path: string): {
  lookups: { attribute: string; table: string | null }[];
  attribute: string;
} | null {
  const steps = path
    .split('.')
    .map((step) => /^(\w+)(?:\[(\w+)\])?$/.exec(step.trim()));
  if (steps.length === 0 || steps.length > 4 || steps.some((step) => !step))
    return null;
  const parsed = steps as RegExpExecArray[];
  const last = parsed[parsed.length - 1];
  return {
    lookups: parsed
      .slice(0, -1)
      .map((step) => ({ attribute: step[1], table: step[2] ?? null })),
    attribute: last[1],
  };
}

/**
 * The view's FetchXML with each binding's condition value set to `{{from}}`, as publish would
 * have written it. A `like` keeps its wildcards; a binding the view has no condition for is added.
 */
export function bindViewFetchXml(
  fetchXml: string,
  bindings: OverlayLookupView['bindings'],
): string {
  let bound = fetchXml;
  for (const binding of bindings ?? []) {
    const at = /^\w+$/.test(binding.at ?? '') ? (binding.at as string) : null;
    const from = /^[\w.@]+$/.test(binding.from ?? '') ? binding.from : null;
    if (!at || !from) continue;
    const operator = /^[\w-]+$/.test(binding.operator ?? '')
      ? (binding.operator as string)
      : 'eq';
    bound = bindCondition(bound, at, from, operator);
  }
  return bound;
}

/** Points the root entity's condition on `at` at `{{from}}`, or adds one when it has none. */
function bindCondition(
  fetchXml: string,
  at: string,
  from: string,
  operator: string,
): string {
  const body = rootEntityBody(fetchXml);
  if (!body) return fetchXml;
  const match = new RegExp(
    `<condition\\b[^>]*\\battribute="${at}"[^>]*?/>`,
  ).exec(rootLevel(fetchXml, body));
  if (!match) {
    const condition = `<filter type="and"><condition attribute="${at}" operator="${operator}" value="{{${from}}}" /></filter>`;
    return (
      fetchXml.slice(0, body.start) + condition + fetchXml.slice(body.start)
    );
  }
  const start = body.start + match.index;
  const original = fetchXml.slice(start, start + match[0].length);
  const value = /\bvalue="([^"]*)"/.exec(original)?.[1] ?? '';
  const wrapped = `${value.startsWith('%') ? '%' : ''}{{${from}}}${value.endsWith('%') && value.length > 1 ? '%' : ''}`;
  const replaced = /\bvalue="/.test(original)
    ? original.replace(/\bvalue="[^"]*"/, `value="${wrapped}"`)
    : `<condition attribute="${at}" operator="${operator}" value="{{${from}}}" />`;
  return (
    fetchXml.slice(0, start) +
    replaced +
    fetchXml.slice(start + match[0].length)
  );
}

/** The root entity's own attributes, in the view's order. */
export function viewColumns(fetchXml: string): OverlayLookupColumn[] {
  const body = rootEntityBody(fetchXml);
  if (!body) return [];
  return [
    ...rootLevel(fetchXml, body).matchAll(/<attribute\s+name="([^"]+)"/g),
  ].map((match) => ({ path: match[1] }));
}

/** Adds a column reached through `hops`; its rows carry it as `<alias>.<attribute>`. */
export function addLinkedColumn(
  fetchXml: string,
  hops: AppUiLookupHop[],
  attribute: string,
  alias: string,
): string {
  const body = rootEntityBody(fetchXml);
  if (!body || hops.length === 0) return fetchXml;
  const link = hops.reduceRight((inner, hop, index) => {
    const name = index === hops.length - 1 ? alias : `${alias}_${index}`;
    return `<link-entity name="${hop.entity}" from="${hop.from}" to="${hop.to}" link-type="outer" alias="${name}">${inner}</link-entity>`;
  }, `<attribute name="${attribute}" />`);
  return fetchXml.slice(0, body.end) + link + fetchXml.slice(body.end);
}

/** Search columns the view lacks are fetched hidden; every column says whether search reads it. */
export function markSearchable(
  fetchXml: string,
  columns: OverlayLookupColumn[],
  searchable: string[] | undefined,
): { fetchXml: string; columns: OverlayLookupColumn[] } {
  if (!searchable)
    return {
      fetchXml,
      columns: columns.map((column) => ({ ...column, searchable: true })),
    };
  const known = new Set(
    columns.flatMap((column) => [column.path, column.field ?? column.path]),
  );
  const missing = searchable.filter(
    (name) => /^\w+$/.test(name) && !known.has(name),
  );
  return {
    fetchXml: ensureAttributes(fetchXml, missing, () => true),
    columns: [
      ...columns.map((column) => ({
        ...column,
        searchable:
          searchable.includes(column.path) ||
          searchable.includes(keyOf(column)),
      })),
      ...missing.map((name) => ({
        path: name,
        searchable: true,
        hidden: true,
      })),
    ],
  };
}
